import { Fragment } from 'react';
import { DIFF_TYPE } from '../../../../../../../../../../constnats/diff';
import { ReportField } from '../../../../../../../../../../constnats/reportConstants';
import type { ReportType } from '../../../../../../../../../../schemas/report.schemas';
import { getReportUniqueBusinessKey } from '../../../../../../../../../../utils/common';
import DescriptorType from '../../../../../../../../../../utils/descriptor/descriptorType';
import DescriptorUtils from '../../../../../../../../../../utils/descriptor/descriptorUtils';
import { reconcileDescriptors, summarizeDescriptors } from '../../../../descriptorDiff';
import { createDescriptorPairLookup, getDiffDescriptorId } from '../../../../descriptorPairs';
import type { DescriptorPairLookup } from '../../../../descriptorPairs';
import { buildReportDiffCellData } from '../../../../reportDiffCellData';
import { buildReportsDiffTableData } from '../../../../reportDiff';
import type {
    BranchComparison,
    DescriptorDiff,
    DescriptorWithDiffType,
    PropertyDiffsByReportType,
    ReportWithDiffType,
} from '../../../../types';
import ReportDiffCell from '../../../reportDiffCell';
import DescriptorCell from '../descriptorCell';
import { ModelContent, ReportHeader, ScrollTable } from '../../tableParts';
import type { ColumnLabels, ComparisonTableProps, GetTooltipContent } from '../../types';

const REPORT_HEADER_FIELDS = [ReportField.YEAR, ReportField.PERIOD];
const PINNED_REPORT_FIELDS = [ReportField.TYPE, ReportField.REPORT_TYPE];
const REPORT_BODY_EXCLUDED_FIELDS = [...REPORT_HEADER_FIELDS, ...PINNED_REPORT_FIELDS];

type DetailedReportsProps = ComparisonTableProps & {
    modelDiffs: DescriptorDiff[];
    columnLabels: ColumnLabels;
    getTooltipContent: GetTooltipContent;
};

function DetailedRows({
    descriptors,
    reports,
    differencesByReport,
    referenceReportsByDisplayedKey,
    modelDiffsById,
    getDescriptorPair,
    columnLabels,
    getTooltipContent,
    excludedFields = REPORT_HEADER_FIELDS,
    level = 0,
}: {
    descriptors: DescriptorWithDiffType[];
    reports: ReportWithDiffType[];
    differencesByReport: PropertyDiffsByReportType;
    referenceReportsByDisplayedKey: Map<string, ReportType>;
    modelDiffsById: Map<string, DescriptorDiff>;
    getDescriptorPair: DescriptorPairLookup;
    columnLabels: ColumnLabels;
    getTooltipContent: GetTooltipContent;
    excludedFields?: string[];
    level?: number;
}) {
    return (
        <>
            {descriptors
                .filter((descriptor) => !excludedFields.includes(descriptor.id))
                .map((descriptor) => {
                    const descriptorPair = getDescriptorPair(descriptor.id);
                    const diffDescriptorId = getDiffDescriptorId(descriptorPair);
                    const modelDiff = modelDiffsById.get(diffDescriptorId);
                    const label = modelDiff?.diffType === DIFF_TYPE.CHANGED ? modelDiff.label : descriptor.label;
                    return <Fragment key={descriptor.id}>
                        <tr className={[
                            DescriptorType.isHeadline(descriptor) ? 'branch-comparison-details__headline' : '',
                            descriptor.diffType ? `branch-comparison-details__row--${descriptor.diffType.toLowerCase()}` : '',
                        ].filter(Boolean).join(' ')}>
                            <DescriptorCell
                                label={label}
                                tooltipContent={getTooltipContent(descriptor.id, label)}
                                descriptorId={descriptor.id}
                                modelDiff={modelDiff}
                                columnLabels={columnLabels}
                                level={level}
                            />
                            {reports.map((report) => {
                                const reportKey = getReportUniqueBusinessKey(report);
                                const fieldDifferences = differencesByReport[reportKey]?.[diffDescriptorId];
                                const referenceReport = referenceReportsByDisplayedKey.get(reportKey);
                                const referenceReportId = report.diffType === DIFF_TYPE.REMOVED
                                    ? report.id
                                    : report.diffType === DIFF_TYPE.CHANGED
                                        ? referenceReport?.id
                                        : undefined;
                                return (
                                    <ReportDiffCell
                                        key={reportKey}
                                        {...buildReportDiffCellData(descriptor, report, descriptorPair, fieldDifferences, referenceReport)}
                                        label={label}
                                        reportName={reportKey}
                                        columnLabels={columnLabels}
                                        targetReportId={report.diffType === DIFF_TYPE.REMOVED ? undefined : report.id}
                                        referenceReportId={referenceReportId}
                                    />
                                );
                            })}
                        </tr>
                        <DetailedRows
                            descriptors={descriptor.subFields}
                            reports={reports}
                            differencesByReport={differencesByReport}
                            referenceReportsByDisplayedKey={referenceReportsByDisplayedKey}
                            modelDiffsById={modelDiffsById}
                            getDescriptorPair={getDescriptorPair}
                            columnLabels={columnLabels}
                            getTooltipContent={getTooltipContent}
                            excludedFields={excludedFields}
                            level={level + 1}
                        />
                    </Fragment>;
                })}
        </>
    );
}

function selectDetailedDescriptors(
    descriptors: DescriptorWithDiffType[],
    comparison: BranchComparison,
    summarized: boolean,
    coreDataDiffOnly: boolean,
    getDescriptorPair: DescriptorPairLookup,
): DescriptorWithDiffType[] {
    if (!summarized) {
        return descriptors;
    }

    const reportSummaryIds = new Set(
        summarizeDescriptors(descriptors, comparison.tableDiff.reportsDiff, getDescriptorPair, coreDataDiffOnly)
            .map((descriptor) => descriptor.id),
    );
    const modelDiffIds = new Set(comparison.tableDiff.schemaDiff.diffs.map((diff) => diff.descriptorId));
    const flatDescriptors = (
        DescriptorUtils.flatDescriptors as (descriptors: DescriptorWithDiffType[]) => DescriptorWithDiffType[]
    )(descriptors);
    return flatDescriptors
        .filter((descriptor) =>
            PINNED_REPORT_FIELDS.includes(descriptor.id) ||
            reportSummaryIds.has(descriptor.id) ||
            modelDiffIds.has(getDiffDescriptorId(getDescriptorPair(descriptor.id))),
        )
        .map((descriptor) => ({ ...descriptor, subFields: [] }));
}

function DetailedReports({
    comparison,
    modelDiffs,
    summarized,
    coreDataDiffOnly,
    companyId,
    columnLabels,
    getTooltipContent,
}: DetailedReportsProps) {
    const { tableDiff, referenceBranch, targetBranch } = comparison;
    const { reports, differencesByReport, referenceReportsByDisplayedKey } = buildReportsDiffTableData(
        referenceBranch.reports,
        targetBranch.reports,
        tableDiff.reportsDiff,
    );
    if (reports.length === 0) {
        return <ModelContent diffs={modelDiffs} columnLabels={columnLabels} getTooltipContent={getTooltipContent} />;
    }

    const getDescriptorPair = createDescriptorPairLookup(tableDiff.descriptorPairs);
    const reconciledDescriptors = reconcileDescriptors(
        referenceBranch.schema.descriptors,
        targetBranch.schema.descriptors,
        tableDiff.schemaDiff,
        getDescriptorPair,
    );
    const descriptors = selectDetailedDescriptors(
        reconciledDescriptors, comparison, summarized, !!coreDataDiffOnly, getDescriptorPair,
    );
    const modelDiffsById = new Map(modelDiffs.map((diff) => [diff.descriptorId, diff]));
    const flatDescriptors = (
        DescriptorUtils.flatDescriptors as (descriptors: DescriptorWithDiffType[]) => DescriptorWithDiffType[]
    )(descriptors);
    const pinnedDescriptors = flatDescriptors
        .filter((descriptor) => PINNED_REPORT_FIELDS.includes(descriptor.id))
        .map((descriptor) => ({ ...descriptor, subFields: [] }));
    const displayedIds = new Set(flatDescriptors
        .filter((descriptor) => !REPORT_HEADER_FIELDS.includes(descriptor.id))
        .map((descriptor) => getDiffDescriptorId(getDescriptorPair(descriptor.id))));
    const missingModelDiffs = modelDiffs.filter((diff) => !displayedIds.has(diff.descriptorId));
    const reportRowsProps = {
        reports,
        differencesByReport,
        referenceReportsByDisplayedKey,
        modelDiffsById,
        getDescriptorPair,
        columnLabels,
        getTooltipContent,
    };

    return (
        <>
            <ScrollTable className="branch-comparison-details__table--reports">
                <thead>
                    <tr>
                        <th className="branch-comparison-details__column--descriptor">Descriptor</th>
                        {reports.map((report) => (
                            <ReportHeader
                                key={getReportUniqueBusinessKey(report)}
                                report={report}
                                label={`${report.year}/${report.period}`}
                                companyId={companyId}
                            />
                        ))}
                    </tr>
                    <DetailedRows descriptors={pinnedDescriptors} {...reportRowsProps} />
                </thead>
                <tbody>
                    <DetailedRows
                        descriptors={descriptors}
                        excludedFields={REPORT_BODY_EXCLUDED_FIELDS}
                        {...reportRowsProps}
                    />
                    {missingModelDiffs.map((diff) => (
                        <tr key={diff.descriptorId} className={`branch-comparison-details__row--${diff.diffType.toLowerCase()}`}>
                            <DescriptorCell
                                label={diff.label}
                                tooltipContent={getTooltipContent(diff.descriptorId, diff.label)}
                                descriptorId={diff.descriptorId}
                                modelDiff={diff}
                                columnLabels={columnLabels}
                            />
                            <td colSpan={reports.length} />
                        </tr>
                    ))}
                </tbody>
            </ScrollTable>
        </>
    );
}

export default DetailedReports;
